import { parse, SDP } from 'sdp-transform';
import intersection from 'lodash/intersection';
import MediaDevices from 'app/services/rtc/MediaDevices';
import WebRTCApi from 'app/services/rtc/WebRTCApi';
import { IClientFeatures, IClientCodecs, AudioCodec, VideoCodec, WebRTCFeatures } from 'app/services/rtc/constants';
import { notify } from 'app/services/errorReporter';
import { decodeAndFilter } from 'app/utils/ioTs';
import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';
import { Set } from 'immutable';

export class PlatformSupport {
  /**
   * Cache the result of supported codecs so that we don't create a new
   * unnecessary `RTCPeerConnection` every time we connect to the signalling
   * server.
   */
  private codecsCache: IClientCodecs | undefined;

  public getSupportedFeatures(): Set<WebRTCFeatures> {
    return Set<WebRTCFeatures>()
      .union(WebRTCApi.getSupportedFeatures())
      .union(MediaDevices.getSupportedFeatures());
  }

  public hasFeatures(required: Set<WebRTCFeatures>) {
    return this.getSupportedFeatures().isSuperset(required);
  }

  private async getSupportedCodecs(): Promise<IClientCodecs> {
    if (this.codecsCache) {
      return this.codecsCache;
    }

    if (!this.getSupportedFeatures().has(WebRTCFeatures.PeerConnection)) {
      return { audioCodecs: [], videoCodecs: [] };
    }

    let connection: RTCPeerConnection | undefined;

    try {
      connection = WebRTCApi.createPeerConnection();
      const offer = await WebRTCApi.createOffer(connection, { audio: true, video: true }, {});

      if (!offer.sdp) {
        // According to the spec `offer.sdp` should only be undefined if
        // `offer.type` is `rollback` so this case should probably never happen.
        notify(new Error('Creating offer for PlatformSupport resulted in an empty SDP'));
        return { audioCodecs: [], videoCodecs: [] };
      }

      const media = parse(offer.sdp).media;
      const audioCodecs = decodeAndFilter(AudioCodec, flattenCodecs(media, 'audio'));
      const videoCodecs = decodeAndFilter(VideoCodec, flattenCodecs(media, 'video'));

      this.codecsCache = { audioCodecs, videoCodecs };
    } catch (error) {
      this.codecsCache = { audioCodecs: [], videoCodecs: [] };

      // Triggered in Android WebViews as "Failed to construct
      // 'RTCPeerConnection': No PeerConnection handler can be
      // created, perhaps WebRTC is disabled?"
      // Don't notify for this known error but do for others.
      //
      // https://app.honeybadger.io/projects/50653/faults/54006967/
      if (error.name !== 'NotSupportedError') {
        notify(error);
      }
    } finally {
      // Make sure the conection is always closed but don't catch
      // the error here
      if (connection) {
        connection.close();
      }
    }

    return this.codecsCache;
  }

  /**
   * Returns description of local features that can be passed to
   * another peer in order to negotiate a connection
   *
   * If minimum set of `requiredFeatures` is not set then
   * `peerConnection` flag will be retuned as `false`.
   */
  public getClientFeatures = async (requiredFeatures: Set<WebRTCFeatures>): Promise<IClientFeatures> => {
    const isSupported = this.hasFeatures(requiredFeatures);
    const { audioCodecs, videoCodecs } = await this.getSupportedCodecs();

    return {
      // Return false here if there are no supported codecs,
      // there's no point in attempting a connection in that case
      peerConnection: isSupported && audioCodecs.length > 0 && videoCodecs.length > 0,
      audioCodecs,
      videoCodecs,
    };
  };
}

/**
 * Creates a flat list of unique codecs for specific media type
 */
const flattenCodecs = (media: SDP['media'], type: 'audio' | 'video') =>
  uniq(
    flatten(
      media
      .filter((element) => element.type === type)
      .map(({ rtp }) => rtp.map(({ codec }) => codec)),
    ),
  );

/**
 * Calculate minimal lowest common denominator of features supported
 * locally with a remote peer's client. For now we only have boolean
 * features but keep in mind some in the future might be numeric (in
 * such case we should calc Math.min for example)
 */
export const featuresIntersection = (here: Partial<IClientFeatures>, there: Partial<IClientFeatures>) => ({
  peerConnection: Boolean(here.peerConnection && there.peerConnection),
  audioCodecs:    intersection(here.audioCodecs || [], there.audioCodecs || []),
  videoCodecs:    intersection(here.videoCodecs || [], there.videoCodecs || []),
});

export const GlobalPlatformSupport = new PlatformSupport();
